import {
  Button,
  FloorbitLogo,
  TabsList,
  Typography,
} from "@facade/components/ui";
import { ChevronsLeft, ChevronsRight } from "lucide-react";

import { Link } from "react-router-dom";
import { ProjectOwnerAvatar } from "../ProjectOwnerAvatar";
import { ProjectSidebarProps } from "./ProjectSidebar.types";
import { ProjectSidebarTab } from "../ProjectSidebarTab";
import { cn } from "@facade/lib/utils";
import { projectTabs } from "./tabInfo";
import { projectsQueries } from "@facade/features/projects/queries";
import { usePageTitle } from "@facade/hooks";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

export function ProjectSidebar({
  projectId,
  open,
  onOpenChange,
}: ProjectSidebarProps) {
  const { t } = useTranslation();

  const { data: project } = useSuspenseQuery(projectsQueries.detail(projectId));
  usePageTitle(project.name);

  const ToggleIcon = open ? ChevronsLeft : ChevronsRight;

  return (
    <aside
      data-open={open}
      className={cn(
        "fixed inset-y-0 left-0 z-50 flex flex-col border-r bg-background",
        "w-(--project-sidebar-width)",
        "transition-[width] duration-150 ease-[cubic-bezier(0.22,1,0.36,1)]",
        "will-change-[width]",
      )}
    >
      <header className="flex flex-col p-3 pt-2 gap-3">
        <div className="flex items-center gap-2.5 w-min">
          <Link to={"/"}>
            <FloorbitLogo className="w-min relative">
              <ProjectOwnerAvatar project={project} />
            </FloorbitLogo>
          </Link>
          {open ? (
            <div className="flex flex-col gap-1">
              <Typography variant="text-md" className="font-semibold">
                Floorbit
              </Typography>
              <Typography
                variant="text-xs"
                className="leading-none whitespace-nowrap overflow-hidden text-ellipsis max-w-37"
              >
                {project.owner.name}
              </Typography>
            </div>
          ) : null}
        </div>
      </header>

      <nav className="flex-1 overflow-hidden p-2">
        <TabsList className="flex h-auto w-full flex-col items-stretch gap-1 bg-transparent! p-0">
          {projectTabs.map((tab) => (
            <ProjectSidebarTab
              key={tab.value}
              value={tab.value}
              icon={tab.icon}
              open={open}
              label={t(tab.labelKey as any)}
            />
          ))}
        </TabsList>
      </nav>

      <footer className="flex p-4 justify-end">
        <Button
          type="button"
          variant="ghost"
          size="icon-xlg"
          aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
          className={cn("w-full justify-center px-2", open && "justify-end")}
          onClick={() => onOpenChange(!open)}
        >
          <ToggleIcon />
        </Button>
      </footer>
    </aside>
  );
}
